import React, {useEffect} from 'react';
import {useForm} from "react-hook-form";
import styles from "../styles.module.scss";
import ModalLayout from "app/components/Layouts/ModalLayout/ModalLayout";
import CloseIcon from "../../../../public/images/close.svg";
import {useTranslation} from "react-i18next";

interface User {
  id: string;
  firstName: string;
  lastName: string;
  title?: string;
  email: string;
  role: string;
  isAdmin: boolean;
}

interface EditFacilityUser {
  isOpen: boolean;
  onClose: () => void;
  user: User;
  onEdit: (user: User) => void;
}

const EditFacilityUser = ({ isOpen, onClose, user, onEdit }: EditFacilityUser) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<User>({ defaultValues: user});
  const { t } = useTranslation();

  /**
   * CFTB-3447: the form is filled from the member every time it opens.
   *
   * This modal is mounted for the life of the row, not created when it opens, so
   * `defaultValues` was read once — at the first render, when the row's own data was
   * all this component ever saw. Submitting then cleared the fields (see below), and
   * because nothing ever filled them again, opening Edit a second time showed an
   * empty form. Re-reading on each open also picks up a member edited a moment ago.
   */
  useEffect(() => {
    if (isOpen) {
      reset(user);
    }
  }, [isOpen, user, reset]);

  // Only a coach profile holds a title, so the field is not offered to the other roles.
  // "coach - admin" is how the list labels a coach who is also an admin.
  const isCoach = user?.role === "coach" || user?.role === "coach - admin";

  const onSubmit = (data: User) => {
    const formatedData = {
      id: user.id,
      firstName: data.firstName,
      lastName: data.lastName,
      // Sent even when blank: that is what clears a title the coach no longer holds.
      ...(isCoach ? { title: data.title?.trim() || "" } : {}),
      email: user.email,
      role: user.role,
      isAdmin: user.isAdmin
    };
    onEdit(formatedData);
    onClose();
    // No blanking reset here: this form edits an existing member rather than
    // collecting a new one, and emptying it was what left the second visit blank.
    // The effect above fills it from the member each time it opens.
  };

  return (
    <ModalLayout isOpen={isOpen} onClose={onClose} classNames={styles.modal}>
      <div className={styles.modal_header}>
        <h2>{t('Edit User')}</h2>
        <img className={styles.modal_status_icon} src={CloseIcon} alt="info" onClick={onClose}/>
      </div>
      <div className={styles.modal_content}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className={styles.form_row}>
            <label htmlFor="first_name">{t('First Name')}</label>
            <input
              id="first_name"
              {...register('firstName', {required: true})}
            />
            {errors.firstName && <span>{t("This field is required")}</span>}
          </div>
          <div className={styles.form_row}>
            <label htmlFor="last_name">{t('Last Name')}</label>
            <input
              id="last_name"
              {...register('lastName', {required: true})}
            />
            {errors.lastName && <span>{t("This field is required")}</span>}
          </div>
          {isCoach && (
            <div className={styles.form_row}>
              <label htmlFor="title">{t('Title')}</label>
              <input
                id="title"
                {...register('title')}
              />
            </div>
          )}
          <div className={styles.form_row}>
            <label htmlFor="email">{t('Email')}</label>
            <input
              id="email"
              {...register('email', {required: true})}
              readOnly={true}
            />
          </div>
          <div className={styles.buttonGroup}>
            <button
              type="button"
              className={styles.cancelButton}
              onClick={onClose}
            >
              {t("Cancel")}
            </button>
            <button type="submit" className={styles.confirmButton}>
              {t("Save")}
            </button>
          </div>
        </form>
      </div>
    </ModalLayout>
  );
};

export default EditFacilityUser;
